import { defineStore, acceptHMRUpdate } from 'pinia'
import { useMiscStore } from './misc'
import { API } from '@/lib/api'
import { fb } from '@/lib/api/firebase'
import { EmailAuthProvider, reauthenticateWithCredential, verifyBeforeUpdateEmail, AuthErrorCodes } from 'firebase/auth'
import { FirebaseError } from 'firebase/app'
import { FirebaseErrors } from '@/lib/enums'


export const useMeStore = defineStore('me', {
	state: () => ({
		_loading: 0,
		members: [],
		subscriptions: [],
		invoices: [],
		profile: {},
		customer: {},
	}),

	actions: {
		async _setLoading(status, global = true) {
			if (status) {
				this.error = null
				this._loading = Math.max(this._loading + 1, 0)
			} else {
				this._loading = Math.max(this._loading - 1, 0)
			}

			if (global) {
				const miscStore = useMiscStore()
				miscStore.setLoading(status)
			}
		},

		async _setError(error = null, global = true) {
			this.error = error
			console.group(`ERROR: meStore`)
			console.warn(error)
			console.groupEnd()

			if (global) {
				const miscStore = useMiscStore()
				miscStore.setError(error)
			}
		},

		async getMe() {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const me = await API.get(`/me`)
					this.profile = me
					resolve(me)
				} catch (error) {
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async getCustomer() {
			this._setLoading(true)
			try {
				const customer = await API.get(`/me/customer`)
				this.customer = customer
				return customer
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}
		},

		async updateCustomer(data) {
			this._setLoading(true)

			try {
				const headers = {
					'If-Match': data[`@odata.etag`]
				}

				const customer = await API.patch(`/me/customer`, data, {}, true, headers)
				this.customer = customer
				return customer
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}
		},

		async getMembers() {
			this._setLoading(true)
			try {
				const members = await API.get(`/me/customer/members`)
				this.members = members
				return members
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}
		},

		async getInvoices() {
			this._setLoading(true)
			try {
				const invoices = await API.get(`/me/customer/invoices`)
				this.invoices = invoices
				return invoices
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}
		},

		async getSubscriptions() {
			this._setLoading(true)
			try {
				const subscriptions = await API.get(`/me/customer/subscriptions`)
				this.subscriptions = subscriptions
				return subscriptions
			} catch (error) {
				this._setError(error)
			} finally {
				this._setLoading(false)
			}
		},

		async addMember(data) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const member = await API.post(`/me/customer/members`, data)
					this.members = this.members ? [...this.members, member] : [member]
					resolve(member)
				} catch (error) {
					reject(error)
					// miscStore.setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async reinviteMember(uid) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					await API.post(`/me/customer/members/${uid}`)
					resolve(true)
				} catch (error) {
					reject(error)
					// miscStore.setError(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async removeMember(uid) {
			this._setLoading(true)

			return new Promise( async (resolve, reject) => {
				try {
					const member = await API.delete(`/me/customer/members/${uid}`)
					this.members = this.members.filter(m => m.uid !== uid)
					resolve(member)
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})

			return this.members
		},

		async updateMember(uid, data) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const headers = {
						'If-Match': data[`@odata.etag`]
					}

					const member = await API.patch(`/me/customer/members/${uid}`, data, {}, true, headers)
					this.members = this.members.map(m => m.uid == uid ? member : m)
					resolve(member)
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async updateContact(data) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const headers = {
						'If-Match': data[`@odata.etag`]
					}

					const profile = await API.patch(`/me`, data, {}, true, headers)
					this.profile = profile
					resolve(profile)
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async updateEmail(data) {
			this._setLoading(true)
			return new Promise(async (resolve, reject) => {
				try {
					const { newEmail, email, password } = data
					const credential = EmailAuthProvider.credential(email, password)

					await reauthenticateWithCredential(fb.auth.currentUser, credential)
					await verifyBeforeUpdateEmail(fb.auth.currentUser, newEmail, {
						url: window.location.href,
						handleCodeInApp: true,
					})
					resolve(true)
				} catch (error) {
					if (error instanceof FirebaseError) {
						error = FirebaseErrors[error.code]
					}
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		updatePassword(data) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {
				try {
					const { etag, ...rest } = data
					const headers = {
						'If-Match': etag
					}

					const profile = await API.patch(`/me/password`, rest, {}, true, headers)
					this.profile = profile
					resolve(profile)
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		async completeCheckout(sessionId) {
			this._setLoading(true)

			return new Promise(async (resolve, reject) => {

				try {
					const session = await API.get(`/users/checkout/${sessionId}`, )
					console.log({ session,})
					resolve(session)
				} catch (error) {
					this._setError(error)
					reject(error)
				} finally {
					this._setLoading(false)
				}
			})
		},

		unsetMembers() {
			this.members = []
		},

		unsetSubscriptions() {
			this.subscriptions = []
		},

		unsetInvoices() {
			this.invoices = []
		},

		unsetProfile() {
			this.profile = {}
		},

		unsetCustomer() {
			this.customer = {}
		},
	},

	getters: {
		isLoading: state => state._loading > 0,
		hasCustomer: state => Object.keys(state.customer).length > 0,
		hasProfile: state => Object.keys(state.profile).length > 0,
	},
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useMeStore, import.meta.hot))
}
